import { Check, Copy, MapPin, PencilLine } from "lucide-react"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { useClipboard } from "@/hooks/use-clipboard"
import { addressForClipboard, addressLines } from "@/lib/format-address"
import { formatDate } from "@/lib/format-datetime"
import type { Locale } from "@/types"
import type { ShipmentAddress, ShipmentContact } from "@/types/shipments"

const COPIED_RESET_MS = 2000

interface ShipmentAddressBlockProps {
  address: ShipmentAddress
  /** For the copy button's aria-label — "Copy address for @handle". */
  creatorLabel: string
  locale: Locale
  /**
   * OKS-985 — prefix for this block's `data-testid`s. Defaults to
   * `"shipments-row"`, so the queue's testids are byte-identical to OKS-984.
   *
   * ⚠️ Exists because this block now renders on TWO pages. Without it,
   * `[data-testid="shipments-row-address"]` matches on both the Shipments
   * queue and the brand redemptions history, and a Playwright locator binds
   * to whichever page happens to be open — a cross-page collision that fails
   * confusingly rather than honestly. The history page passes
   * `"redemption-row"`, joining the family already there
   * (`redemption-row-action-mark-shipped` et al.).
   */
  testIdPrefix?: string
  /**
   * OKS-991 — the creator's email and phone, disclosed by the SAME gate as the
   * address (PRD R3/R4).
   *
   * ⚠️ OPTIONAL because two of this component's three call sites do not pass
   * it: the queue renders contact at PARCEL level, while Waiting and
   * Recently-shipped rows and the redemptions history show the address alone.
   * Optional here is NOT the OKS-933 hazard — nothing is silently dropped,
   * because the contact lines simply do not exist on those surfaces. The
   * gating decision is the server's; this component only renders what it is
   * handed.
   */
  contact?: ShipmentContact
}

/**
 * OKS-984 — the destination block on a Shipments row (UX Spec §2.1).
 *
 * # ⚠️ TWO unconfirmed treatments, never one
 *
 * PO's ruling (UX Spec §1, amended): `origin` is checked FIRST and `profile`
 * is terminal.
 *
 *   snapshot + confirmed  → no label
 *   snapshot + !confirmed → NEUTRAL "Not confirmed by creator" (secondary)
 *   profile   + any       → CAUTIONARY amber "Profile address — unconfirmed"
 *
 * The two mean opposite things to an operator: the first is "a real address
 * the creator typed, ship it"; the second is "our guess, check before you post
 * it". **One generic "unconfirmed" treatment would be swappable without any
 * assertion noticing**, which is why they carry distinct testids rather than
 * one testid with a variant.
 *
 * The direction is not arbitrary. `profile + confirmed` would mean we hold an
 * address the creator never supplied for this parcel while something claims it
 * is vouched for; reading `confirmed` first would render that as "confirmed,
 * no label" — the one branch that can put a parcel on a wrong doorstep. When
 * the two fields disagree, the cautious reading wins.
 *
 * # A11y
 *
 * Semantic `<address>`. The label is a WORD plus an icon, never colour alone.
 * The copy button names the creator. Copy outcome is announced via `sonner`
 * (polite live region), not by the icon swap alone.
 */
// OKS-1036 — literal keys, not a template string, so the locale sweep can see
// both and a new source value is a type error here rather than a missing key.
const SOURCE_KEYS: Record<
  NonNullable<ShipmentAddress["confirmation_source"]>,
  string
> = {
  creator: "shipments.address.source_creator",
  profile_auto: "shipments.address.source_profile_auto",
  // OKS-1037 — neutral, never amber (UX §6); the brand's own write.
  brand: "shipments.address.source_brand",
}

export function ShipmentAddressBlock({
  address,
  creatorLabel,
  locale,
  testIdPrefix = "shipments-row",
  contact,
}: ShipmentAddressBlockProps) {
  const { t } = useTranslation()
  const [, copy] = useClipboard()
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!copied) return
    const timer = setTimeout(() => setCopied(false), COPIED_RESET_MS)
    return () => clearTimeout(timer)
  }, [copied])

  // OKS-994 — `corrected_at` is a DATE, so it takes the date-only formatter.
  // `formatDateTime` would print « 3 sept. 2026 00:00 » and invite an operator
  // to read a real time out of a parsed midnight.
  const correctedOn = formatDate(address.corrected_at, locale)

  // ⚠️ origin FIRST, profile terminal. See the component docstring.
  const isProfile = address.origin === "profile"
  const showNeutralNote = !isProfile && !address.confirmed
  // OKS-1036 — WHO confirmed, on confirmed SNAPSHOT rows only (UX Spec §3).
  // Same origin-first discipline: a legacy `profile + confirmed` row keeps its
  // amber label and gets no source line, so the two never share a row. The
  // exact complement of `showNeutralNote` on the snapshot side, which is why
  // the line sits in the same slot. Gear redemptions carry null → no line.
  const sourceKey =
    !isProfile && address.confirmed && address.confirmation_source
      ? SOURCE_KEYS[address.confirmation_source]
      : null

  async function handleCopy() {
    const ok = await copy(addressForClipboard(address))

    if (ok) {
      setCopied(true)
      toast.success(t("shipments.address.copied"))
    } else {
      // The address stays selectable text, so the operator is not blocked.
      toast.error(t("shipments.address.copy_failed"))
    }
  }

  return (
    <div
      className="bg-muted/40 flex flex-col gap-2 rounded-xl p-3"
      data-testid={`${testIdPrefix}-address`}
    >
      <div className="text-muted-foreground flex flex-wrap items-center gap-1.5 text-xs font-medium">
        <MapPin className="size-3.5 shrink-0" aria-hidden="true" />
        {t("shipments.address.ship_to")}

        {isProfile && (
          <Badge
            className="border-amber-300 bg-amber-100 text-amber-900"
            data-testid={`${testIdPrefix}-address-note-profile`}
          >
            {t("shipments.address.profile_unconfirmed")}
          </Badge>
        )}

        {showNeutralNote && (
          <Badge
            variant="secondary"
            data-testid={`${testIdPrefix}-address-note-unconfirmed`}
          >
            {t("shipments.address.not_confirmed")}
          </Badge>
        )}
      </div>

      {/* OKS-1036 — provenance line. Secondary text, never a badge and never
          amber: the row is shippable either way (PRD OQ9). Its own line under
          « Livrer à » so it wraps at 375px and the OKS-994 badge below stacks
          beneath it; after a creator edit both render — "auto-confirmed, then
          corrected" (UX Spec §3). */}
      {sourceKey && (
        <p
          className="text-muted-foreground text-xs"
          data-testid={`${testIdPrefix}-address-source`}
        >
          {t(sourceKey)}
        </p>
      )}

      {/* OKS-994 — FRESHNESS, and it answers a different question from the two
          provenance labels above: those say where the address came from, this
          says it MOVED since an operator may have copied it. It coexists with
          them and never replaces them (UX Spec §S3).

          ⚠️ Between the « Ship to » header and the address, never inline in
          the header row: at 320/402px a badge plus its hint beside the label
          squeezes the address to two words per line, and a wrapped address is
          a mis-shipped parcel (UX Spec §5). Column by default, inline from
          768px.

          ⚠️ Text + icon, never colour alone (UX Spec §6) — an operator with no
          colour perception loses nothing. No entrance animation and no live
          region: it is static server-rendered content, and announcing it would
          interrupt an operator mid-copy (§4). */}
      {address.corrected && (
        <div
          className="flex flex-col gap-1 md:flex-row md:items-center md:gap-2"
          data-testid={`${testIdPrefix}-address-corrected`}
        >
          <Badge
            variant="secondary"
            className="w-fit"
            aria-label={t("shipments.address.corrected_aria", {
              creator: creatorLabel,
            })}
          >
            <PencilLine className="size-3.5 shrink-0" aria-hidden="true" />
            {t("shipments.address.corrected")}
          </Badge>
          {/* The hint carries the DATE and the instruction to re-copy. Rendered
              only when the date formats: a marker that says « corrigée le »
              with nothing after it reads as a bug, and `corrected_at` is
              non-null by construction whenever `corrected` is true. */}
          {correctedOn && (
            <span className="text-muted-foreground text-xs">
              {t("shipments.address.corrected_hint", { date: correctedOn })}
            </span>
          )}
        </div>
      )}

      <address className="text-sm not-italic">
        {addressLines(address, locale).map((line, index) => (
          <span
            key={line}
            className={
              index === 0
                ? "block font-semibold"
                : "text-muted-foreground block"
            }
          >
            {line}
          </span>
        ))}
      </address>

      {/* OKS-991 — contact sits BELOW the address and ABOVE the copy button:
          reading order is destination, then how to reach them, then the action
          (UX Spec §6). The copy button stays address-only (PRD OQ-8) — a
          shipping label takes no email. */}
      {contact && (
        <ContactLines contact={contact} testIdPrefix={testIdPrefix} />
      )}

      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="self-start"
        // ⚠️ `void` — the handler is async and `onClick` expects a void
        // return. Without it eslint's no-misused-promises fires, and the
        // rejection would be unhandled rather than surfaced by the toast.
        onClick={() => void handleCopy()}
        aria-label={t("shipments.address.copy_aria", { creator: creatorLabel })}
        data-testid={`${testIdPrefix}-copy-address`}
      >
        {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
        {copied
          ? t("shipments.address.copied_short")
          : t("shipments.address.copy")}
      </Button>
    </div>
  )
}

/**
 * OKS-991 — « E-mail » and « Téléphone » under the address.
 *
 * ⚠️ Both labels are TEXT, never icon-only, and the no-phone state is a
 * SENTENCE rather than a dash or an empty slot (UX Spec §6). An empty slot is
 * indistinguishable from a component that failed to render, both to a screen
 * reader and to an operator deciding whether to chase the creator.
 *
 * ⚠️ `tel:` / `mailto:` are LINKS, not buttons — they hand off to the OS
 * handler, and a button would strip that affordance from keyboard and
 * assistive users for no gain.
 */
function ContactLines({
  contact,
  testIdPrefix,
}: {
  contact: ShipmentContact
  testIdPrefix: string
}) {
  const { t } = useTranslation()

  return (
    <dl
      className="flex flex-col gap-1 text-sm sm:grid sm:grid-cols-[auto_1fr] sm:gap-x-3"
      data-testid={`${testIdPrefix}-contact`}
    >
      <dt className="text-muted-foreground text-xs font-medium">
        {t("shipments.parcel.contact.email")}
      </dt>
      <dd>
        {contact.email ? (
          <a
            href={`mailto:${contact.email}`}
            className="break-all underline underline-offset-2"
            data-testid={`${testIdPrefix}-contact-email`}
          >
            {contact.email}
          </a>
        ) : (
          <span
            className="text-muted-foreground"
            data-testid={`${testIdPrefix}-contact-email-absent`}
          >
            {t("shipments.parcel.contact.email_absent")}
          </span>
        )}
      </dd>

      <dt className="text-muted-foreground text-xs font-medium">
        {t("shipments.parcel.contact.phone")}
      </dt>
      <dd className="flex flex-wrap items-center gap-1.5">
        {contact.phone ? (
          <>
            <a
              href={`tel:${contact.phone.number}`}
              className="break-all underline underline-offset-2"
              data-testid={`${testIdPrefix}-contact-phone`}
            >
              {contact.phone.number}
            </a>
            {/* ⚠️ The badge is the whole point of the two-fact shape. An
                unlabelled profile number claims the creator gave it for THIS
                parcel; they did not. */}
            {contact.phone.origin === "profile" && (
              <Badge
                variant="secondary"
                data-testid={`${testIdPrefix}-contact-phone-profile`}
              >
                {t("shipments.parcel.contact.phone_from_profile")}
              </Badge>
            )}
          </>
        ) : (
          <span
            className="text-muted-foreground"
            data-testid={`${testIdPrefix}-contact-phone-absent`}
          >
            {t("shipments.parcel.contact.phone_absent")}
          </span>
        )}
      </dd>
    </dl>
  )
}
